// /home — the tenant landing page (mockup 20, "Inicio"). The owner's at-a-glance
// panorama, rendered inside AppLayout (the shell provides nav, top bar, logout).
//
// The greeting keeps the exact strings "Hola, {name}" + "Bienvenido, {name}" and
// data-testid="home" — the e2e/login.spec.ts contract (memory:
// feedback_pages_e2e_copy_contract). Data via the getHome() seam (mock); the
// "Escríbele" action opens the concierge thread at /mensajes.

import { useQuery } from '@tanstack/react-query'
import { type ReactNode, useEffect } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import {
  type AttentionIcon,
  type AttentionItem,
  getHome,
  type HomeData,
  type QuickLink,
  type QuickLinkIcon,
} from './home-data.js'
import { useSession } from '../auth/session.js'
import { useAuthStore } from '../auth/store.js'
import { Button, KpiTile, Spinner } from '../design-system/index.js'

const ATTENTION_ICON: Record<AttentionIcon, ReactNode> = {
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.5 2" />
    </>
  ),
  prospect: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4 4-6 8-6s8 2 8 6" />
    </>
  ),
  team: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M19 8v6M22 11h-6" />
    </>
  ),
}

// The manager card's blurb, role-aware. Only the roles that can reach the
// concierge thread are told to write; an Asesor reading "escríbele
// directamente" would be following an instruction the app then refuses. Both
// variants come from the same flag that shows/hides the button below them, so
// the copy and the action can never contradict each other. Neither names the
// manager: their name is already in bold directly above, and the fallback
// ("Tu equipo INHERTA") doesn't read as a person.
const MANAGER_BLURB_MANAGE =
  'Cualquier duda sobre tu cuenta, tu bot o tu facturación, escríbele directamente. Te responde en menos de 2 horas hábiles.'
const MANAGER_BLURB_READONLY =
  'Acompaña a tu espacio de trabajo desde INHERTA. Si necesitas algo de tu cuenta o tu facturación, avísale a quien administra tu espacio.'

const QUICKLINK_ICON: Record<QuickLinkIcon, ReactNode> = {
  inbox: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />,
  leads: <path d="M3 6h18M3 12h18M3 18h12" />,
  reports: <path d="M4 19V9m5 10V5m5 14v-7m5 7V8" />,
}

function Stroke({ children, className }: { children: ReactNode; className: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={className}
    >
      {children}
    </svg>
  )
}

function Chevron(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="size-[15px] flex-none text-text-3"
    >
      <path d="m9 6 6 6-6 6" />
    </svg>
  )
}

function AttentionRow({
  item,
  onClick,
}: {
  item: AttentionItem
  onClick: () => void
}): JSX.Element {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center gap-3 border-b border-line px-5 py-3.5 text-left transition-colors last:border-b-0 hover:bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-blue"
      >
        <span
          aria-hidden
          className={`grid size-9 flex-none place-items-center rounded-[10px] [&_svg]:size-4 ${
            item.tone === 'warn' ? 'bg-warn-bg text-warn' : 'bg-field text-text-2'
          }`}
        >
          <Stroke className="size-4">{ATTENTION_ICON[item.icon]}</Stroke>
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[12.5px] leading-snug text-text">{item.text}</span>
          <span className="mt-0.5 block text-[11px] text-text-2">{item.meta}</span>
        </span>
        <Chevron />
      </button>
    </li>
  )
}

function QuickLinkRow({ link, onClick }: { link: QuickLink; onClick: () => void }): JSX.Element {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center gap-3 border-b border-line px-5 py-3 text-left transition-colors last:border-b-0 hover:bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-blue"
      >
        <span
          aria-hidden
          className="grid size-8 flex-none place-items-center rounded-lg bg-field text-text-2"
        >
          <Stroke className="size-[15px]">{QUICKLINK_ICON[link.icon]}</Stroke>
        </span>
        <span className="flex-1 text-[12.5px] font-medium text-text">{link.label}</span>
        <Chevron />
      </button>
    </li>
  )
}

// Turns an unknown fetch error into a short, legible signal (HTTP status, or a
// transport code like ERR_NETWORK / ECONNABORTED) so a failure is diagnosable.
function homeErrorDetail(err: unknown): string {
  const e = err as { response?: { status?: number }; code?: string; message?: string } | undefined
  if (e?.response?.status) return `HTTP ${e.response.status}`
  if (e?.code) return e.code
  return e?.message ?? 'desconocido'
}

// The owner's #1 confidence signal: is the agent actually answering on WhatsApp?
// Green when a number is connected AND a bot is active; amber otherwise, with the
// reason (their manager connects the channel).
function AgentStatusCard({
  agent,
  onWriteManager,
}: {
  agent: HomeData['agent']
  /** Absent for roles that can't reach the concierge thread (Asesor/Visor):
   *  the card still says what's wrong, it just doesn't hand them a button onto
   *  a 403 or tell them to write to a manager they can't write to. */
  onWriteManager?: () => void
}): JSX.Element {
  if (agent.live) {
    return (
      <div className="mt-5 flex flex-wrap items-center gap-3 rounded-xl border border-ok/25 bg-ok-bg px-4 py-3.5">
        <span className="inline-block size-2 flex-none rounded-full bg-ok" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-text">Tu agente está activo en WhatsApp</p>
          <p className="text-[12px] leading-snug text-text-2">
            {agent.whatsappNumber
              ? `Responde en ${agent.whatsappNumber}, las 24 horas.`
              : 'Responde a tus clientes las 24 horas.'}
          </p>
        </div>
      </div>
    )
  }
  const reason =
    agent.status === 'no_channel'
      ? 'Tu agente aún no está conectado a WhatsApp. Tu gerente lo conecta por ti.'
      : onWriteManager
        ? 'Tu agente está en pausa y no está respondiendo. Escríbele a tu gerente para reactivarlo.'
        : 'Tu agente está en pausa y no está respondiendo. Avísale a quien administra tu espacio de trabajo.'
  return (
    <div className="mt-5 flex flex-wrap items-center gap-3 rounded-xl border border-warn/25 bg-warn-bg px-4 py-3.5">
      <span className="inline-block size-2 flex-none rounded-full bg-warn" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-semibold text-text">Tu agente aún no está en línea</p>
        <p className="text-[12px] leading-snug text-text-2">{reason}</p>
      </div>
      {onWriteManager && (
        <Button size="sm" variant="secondary" onClick={onWriteManager}>
          Escríbele a tu gerente
        </Button>
      )}
    </div>
  )
}

export function HomePage(): JSX.Element {
  const user = useAuthStore((s) => s.user)
  const navigate = useNavigate()
  // The concierge thread (/mensajes) belongs to owner/admin — every /concierge
  // endpoint requires `tenant.concierge`. The manager card stays for everyone
  // (knowing who your account manager is, is information), but the button that
  // writes to them only appears for the roles that can.
  const canWriteManager = useSession()?.canManageWorkspace === true
  // Set by the wizard's "Ver después" so Home stops re-redirecting this
  // session; the banner takes over as the re-entry point.
  const skippedOnboarding = sessionStorage.getItem('onboarding-skipped') === '1'
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['home'],
    queryFn: getHome,
  })
  const name = user?.name ?? ''

  // Diagnostic: log the actual failure so we can see WHAT breaks (status / code)
  // rather than guess. Shows up in the browser console.
  useEffect(() => {
    if (isError) console.error('[home] load failed:', error)
  }, [isError, error])

  return (
    <div data-testid="home" className="mx-auto w-full max-w-5xl">
      {/* Greeting — keeps the exact e2e copy (Hola + Bienvenido). */}
      <p className="text-[12.5px] font-medium text-text-2">Hola, {name}</p>
      <h1 className="mt-1 text-[22px] font-bold tracking-[-0.027em] text-text">
        Bienvenido, {name}
      </h1>
      {data && <p className="mt-1.5 text-[13px] leading-relaxed text-text-2">{data.summary}</p>}

      {isError && !data ? (
        <div className="mt-8 text-[13px] text-text-2">
          <p>No pudimos cargar tu panorama.</p>
          <p className="mt-1 text-[11.5px] text-text-3">Detalle: {homeErrorDetail(error)}</p>
          <Button className="mt-3" variant="secondary" onClick={() => void refetch()}>
            Reintentar
          </Button>
        </div>
      ) : isLoading || !data ? (
        <div className="mt-8">
          <Spinner label="Cargando tu panorama…" />
        </div>
      ) : data.onboardingPending && !skippedOnboarding ? (
        // C1: a brand-new owner shouldn't LAND on a dashboard of zeros with a
        // banner they may not read — first run goes straight to the wizard.
        // "Ver después" there sets the session flag, and this banner becomes
        // the re-entry.
        <Navigate to="/onboarding" replace />
      ) : (
        <>
          {data.onboardingPending ? (
            <div className="mt-5 flex flex-wrap items-center gap-3 rounded-xl border border-brand-blue/20 bg-brand-blue/[0.05] px-4 py-3.5">
              <span
                aria-hidden
                className="grid size-8 flex-none place-items-center rounded-lg bg-brand-blue/10 text-link [&_svg]:size-[16px]"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 13l4 4L19 7" />
                </svg>
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold text-text">
                  Termina de configurar tu agente
                </p>
                <p className="text-[12px] leading-snug text-text-2">
                  Completa los primeros pasos para poner tu agente a atender en WhatsApp.
                </p>
              </div>
              <Button size="sm" onClick={() => void navigate('/onboarding')}>
                Continuar configuración
              </Button>
            </div>
          ) : (
            <AgentStatusCard
              agent={data.agent}
              // Spread, not `onWriteManager={cond ? fn : undefined}`: under
              // exactOptionalPropertyTypes an explicit undefined is not "absent".
              {...(canWriteManager ? { onWriteManager: () => void navigate('/mensajes') } : {})}
            />
          )}

          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {data.kpis.map((k) => (
              <KpiTile key={k.id} label={k.label} value={k.value} delta={k.delta} />
            ))}
          </div>

          <div className="mt-4 grid grid-cols-1 items-start gap-3 lg:grid-cols-3">
            {/* needs your attention — spans the first two columns so the side
                column lines up under the third KPI tile above. */}
            <section
              className="rounded-xl border border-line bg-surface lg:col-span-2"
              aria-labelledby="att-title"
            >
              <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-3.5">
                <h2 id="att-title" className="text-[13.5px] font-bold text-text">
                  Necesita tu atención
                </h2>
                <span className="text-[11.5px] font-semibold text-text-3">
                  {data.attention.length} pendientes
                </span>
              </div>
              <ul>
                {data.attention.map((a) => (
                  <AttentionRow key={a.id} item={a} onClick={() => void navigate(a.to)} />
                ))}
              </ul>
            </section>

            {/* side column */}
            <div className="flex flex-col gap-4">
              <section className="rounded-xl border border-line bg-surface p-5">
                <div className="flex items-center gap-3">
                  <span
                    aria-hidden
                    className="grid size-10 flex-none place-items-center rounded-full bg-[image:var(--brand-gradient)] text-[12px] font-bold text-white"
                  >
                    {data.manager.initials}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[13px] font-bold text-text">
                      {data.manager.name}
                    </span>
                    <span className="block text-[11.5px] text-text-3">
                      Tu gerente de cuenta en INHERTA
                    </span>
                  </span>
                </div>
                <p className="mt-3 text-[12px] leading-relaxed text-text-2">
                  {canWriteManager ? MANAGER_BLURB_MANAGE : MANAGER_BLURB_READONLY}
                </p>
                {canWriteManager && (
                  <Button
                    variant="secondary"
                    size="sm"
                    block
                    className="mt-3.5"
                    iconStart={
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden
                        className="size-[15px]"
                      >
                        <rect x="2" y="4.5" width="20" height="15" rx="2.6" />
                        <path d="m2.7 6.5 9.3 6 9.3-6" />
                      </svg>
                    }
                    onClick={() => void navigate('/mensajes')}
                  >
                    Escríbele a {data.manager.name.split(' ')[0]}
                  </Button>
                )}
              </section>

              <section
                className="rounded-xl border border-line bg-surface"
                aria-labelledby="ql-title"
              >
                <h2
                  id="ql-title"
                  className="border-b border-line px-5 py-3 text-[11px] font-bold uppercase tracking-[0.05em] text-text-3"
                >
                  Accesos rápidos
                </h2>
                <ul>
                  {data.quickLinks.map((q) => (
                    <QuickLinkRow key={q.id} link={q} onClick={() => void navigate(q.to)} />
                  ))}
                </ul>
              </section>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
